import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import type { TodoCategory } from '../../types/todo.js';
import * as categoryApi from '../../services/category-api.js';

const PRESET_COLORS = [
  '#00D4FF', '#22C55E', '#F59E0B', '#EF4444',
  '#8B5CF6', '#EC4899', '#06B6D4', '#F97316',
  '#14B8A6', '#6366F1', '#A855F7', '#E11D48',
];

@customElement('cp-category-settings')
export class CpCategorySettings extends LitElement {
  createRenderRoot() { return this; }

  @state() private _categories: TodoCategory[] = [];
  @state() private _loading = true;
  @state() private _error = '';

  // Add form
  @state() private _addMode = false;
  @state() private _addName = '';
  @state() private _addColor = PRESET_COLORS[0];
  @state() private _addSubmitting = false;

  // Edit state
  @state() private _editId: number | null = null;
  @state() private _editName = '';
  @state() private _editColor = '';
  @state() private _editSubmitting = false;

  // Delete state
  @state() private _deleteError = '';

  connectedCallback(): void {
    super.connectedCallback();
    void this._loadCategories();
  }

  private async _loadCategories(): Promise<void> {
    this._loading = true;
    this._error = '';
    try {
      this._categories = await categoryApi.getCategories();
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Kategorien konnten nicht geladen werden';
    } finally {
      this._loading = false;
    }
  }

  // --- Add handlers ---

  private _openAdd(): void {
    this._addMode = true;
    this._addName = '';
    this._addColor = PRESET_COLORS[0];
    this._deleteError = '';
    this.updateComplete.then(() => {
      const input = this.querySelector<HTMLInputElement>('.cp-cat__add-input');
      input?.focus();
    });
  }

  private _cancelAdd(): void {
    this._addMode = false;
  }

  private _onAddNameInput(e: Event): void {
    this._addName = (e.target as HTMLInputElement).value;
  }

  private _onAddColorSelect(color: string): void {
    this._addColor = color;
  }

  private async _submitAdd(): Promise<void> {
    const name = this._addName.trim();
    if (!name || this._addSubmitting) return;

    this._addSubmitting = true;
    try {
      await categoryApi.createCategory({ name, color: this._addColor });
      this._addMode = false;
      await this._loadCategories();
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Kategorie konnte nicht erstellt werden';
    } finally {
      this._addSubmitting = false;
    }
  }

  private _onAddKeyDown(e: KeyboardEvent): void {
    if (e.key === 'Enter') {
      void this._submitAdd();
    } else if (e.key === 'Escape') {
      this._cancelAdd();
    }
  }

  // --- Edit handlers ---

  private _startEdit(cat: TodoCategory): void {
    this._editId = cat.id;
    this._editName = cat.name;
    this._editColor = cat.color;
    this._deleteError = '';
    this.updateComplete.then(() => {
      const input = this.querySelector<HTMLInputElement>('.cp-cat__edit-input');
      input?.focus();
    });
  }

  private _cancelEdit(): void {
    this._editId = null;
  }

  private _onEditNameInput(e: Event): void {
    this._editName = (e.target as HTMLInputElement).value;
  }

  private _onEditColorSelect(color: string): void {
    this._editColor = color;
  }

  private async _submitEdit(): Promise<void> {
    if (this._editId === null || this._editSubmitting) return;
    const name = this._editName.trim();
    if (!name) return;

    this._editSubmitting = true;
    try {
      await categoryApi.updateCategory(this._editId, { name, color: this._editColor });
      this._editId = null;
      await this._loadCategories();
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Kategorie konnte nicht aktualisiert werden';
    } finally {
      this._editSubmitting = false;
    }
  }

  private _onEditKeyDown(e: KeyboardEvent): void {
    if (e.key === 'Enter') {
      void this._submitEdit();
    } else if (e.key === 'Escape') {
      this._cancelEdit();
    }
  }

  // --- Delete handler ---

  private async _deleteCategory(cat: TodoCategory): Promise<void> {
    this._deleteError = '';
    try {
      await categoryApi.deleteCategory(cat.id);
      await this._loadCategories();
    } catch (e) {
      this._deleteError = e instanceof Error ? e.message : 'Kategorie konnte nicht geloescht werden';
    }
  }

  // --- Render ---

  private _renderColorPicker(selectedColor: string, onSelect: (color: string) => void) {
    return html`
      <div class="cp-cat__colors">
        ${PRESET_COLORS.map(color => html`
          <button
            class="cp-cat__color-swatch ${color === selectedColor ? 'cp-cat__color-swatch--selected' : ''}"
            style="background-color: ${color}"
            @click=${() => onSelect(color)}
            type="button"
          ></button>
        `)}
      </div>
    `;
  }

  private _renderCategoryRow(cat: TodoCategory) {
    if (this._editId === cat.id) {
      return html`
        <div class="cp-cat__row cp-cat__row--editing">
          <span class="cp-cat__color-dot" style="background-color: ${this._editColor}"></span>
          <input
            class="cp-cat__edit-input"
            type="text"
            .value=${this._editName}
            @input=${this._onEditNameInput}
            @keydown=${this._onEditKeyDown}
          />
          <div class="cp-cat__row-actions">
            <button class="cp-cat__action-btn cp-cat__action-btn--save" @click=${this._submitEdit} ?disabled=${this._editSubmitting || !this._editName.trim()}>Speichern</button>
            <button class="cp-cat__action-btn cp-cat__action-btn--cancel" @click=${this._cancelEdit}>Abbrechen</button>
          </div>
          ${this._renderColorPicker(this._editColor, (c) => this._onEditColorSelect(c))}
        </div>
      `;
    }

    return html`
      <div class="cp-cat__row">
        <span class="cp-cat__color-dot" style="background-color: ${cat.color}"></span>
        <span class="cp-cat__name">${cat.name}</span>
        <div class="cp-cat__row-actions">
          <button class="cp-cat__action-btn cp-cat__action-btn--edit" @click=${() => this._startEdit(cat)} title="Bearbeiten">&#9998;</button>
          <button class="cp-cat__action-btn cp-cat__action-btn--delete" @click=${() => this._deleteCategory(cat)} title="Loeschen">&times;</button>
        </div>
      </div>
    `;
  }

  render() {
    return html`
      <div class="cp-cat">
        <div class="cp-cat__header">
          <h3 class="cp-cat__title">To-Do Kategorien</h3>
          ${!this._addMode ? html`
            <button class="cp-cat__add-btn" @click=${this._openAdd}>+ Neue Kategorie</button>
          ` : nothing}
        </div>

        ${this._deleteError ? html`
          <div class="cp-cat__error">${this._deleteError}</div>
        ` : nothing}

        ${this._error ? html`
          <div class="cp-cat__error">${this._error}</div>
        ` : nothing}

        ${this._loading ? html`
          <p class="cp-cat__loading">Kategorien werden geladen...</p>
        ` : html`
          <div class="cp-cat__list">
            ${this._categories.length === 0 && !this._addMode ? html`
              <p class="cp-cat__empty">Noch keine Kategorien vorhanden.</p>
            ` : nothing}

            ${this._categories.map(cat => this._renderCategoryRow(cat))}

            ${this._addMode ? html`
              <div class="cp-cat__row cp-cat__row--adding">
                <span class="cp-cat__color-dot" style="background-color: ${this._addColor}"></span>
                <input
                  class="cp-cat__add-input"
                  type="text"
                  placeholder="Kategorie-Name..."
                  .value=${this._addName}
                  @input=${this._onAddNameInput}
                  @keydown=${this._onAddKeyDown}
                />
                <div class="cp-cat__row-actions">
                  <button class="cp-cat__action-btn cp-cat__action-btn--save" @click=${this._submitAdd} ?disabled=${this._addSubmitting || !this._addName.trim()}>Erstellen</button>
                  <button class="cp-cat__action-btn cp-cat__action-btn--cancel" @click=${this._cancelAdd}>Abbrechen</button>
                </div>
                ${this._renderColorPicker(this._addColor, (c) => this._onAddColorSelect(c))}
              </div>
            ` : nothing}
          </div>
        `}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-category-settings': CpCategorySettings;
  }
}
